import { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import type { DPHData } from '@/types';
import type { NotificationFrequency } from '@/types/dph';

import { isValidEmail } from '../utils/dph.utils';

interface DPHEditModalProps {
  data: DPHData;
  type: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  notificationFrequency: NotificationFrequency[];
  onSave: (data: {
    notificationEmails: string[];
    notificationFrequencyId: number;
    threshold: number;
  }) => void;
}

/**
 * Modal for editing DPH threshold, notification frequency, and email list
 * Supports both Licensed Period and Monthly types
 */
export const DPHEditModal = ({
  data,
  type,
  isOpen,
  setIsOpen,
  notificationFrequency,
  onSave,
}: DPHEditModalProps) => {
  // Determine which data to use based on type
  let initialNotificationFrequencyId: number;
  let initialThresholdPercentage: number;
  let initialNotificationEmails: string[];

  if (type === 'LicensedPeriod') {
    initialNotificationFrequencyId = data?.licensedPeriodDPH?.notificationFrequencyId || 1;
    initialThresholdPercentage = data?.licensedPeriodDPH?.thresholdPercentage || 80;
    initialNotificationEmails = data?.licensedPeriodDPH?.notificationEmails || [];
  } else {
    const latestMonth =
      data?.monthlyDPH?.months && data.monthlyDPH.months.length > 0
        ? data.monthlyDPH.months[data.monthlyDPH.months.length - 1]
        : null;
    initialNotificationFrequencyId = latestMonth?.notificationFrequencyId || 1;
    initialThresholdPercentage = latestMonth?.thresholdPercentage || 80;
    initialNotificationEmails = data?.monthlyDPH?.notificationEmails || [];
  }

  const [threshold, setThreshold] = useState<string>(initialThresholdPercentage.toString());
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [thresholdError, setThresholdError] = useState<string>('');
  const [emails, setEmails] = useState<string[]>(initialNotificationEmails);
  const [emailFrequency, setEmailFrequency] = useState<number>(initialNotificationFrequencyId);

  // Reset state when type changes
  useEffect(() => {
    setEmail('');
    setError('');
    setThresholdError('');
    setEmails(initialNotificationEmails);
    setEmailFrequency(initialNotificationFrequencyId);
    setThreshold(initialThresholdPercentage.toString());
  }, [type, initialNotificationFrequencyId, initialThresholdPercentage, initialNotificationEmails]);

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setThresholdError('');
    const { value } = e.target;

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      setThresholdError('Value must be between 0 and 100');
    }

    if (!value) {
      setThresholdError("Value can't be empty");
    }

    setThreshold(value);
  };

  const addEmail = () => {
    setError('');

    if (!isValidEmail(email)) {
      setError('Please enter a valid email');
      return;
    }

    // Check for duplicates (case-insensitive)
    const isEmailExist = emails.some((e) => e.toLowerCase() === email.toLowerCase());

    if (isEmailExist) {
      setError('Email id already exists!!!');
      return;
    }

    setEmails([email, ...emails]);
    setEmail('');
  };

  const removeEmail = (index: number) => {
    if (emails.length === 1) {
      setError('One email address is required');
      return;
    }

    const newArray = emails.filter((_item, i) => i !== index);
    setEmails(newArray);
  };

  const handleSubmit = () => {
    const thresholdDecimal = parseFloat(threshold) / 100;

    onSave({
      notificationEmails: emails,
      notificationFrequencyId: emailFrequency,
      threshold: thresholdDecimal,
    });

    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Prevent closing on backdrop click or escape key
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    handleClose();
  };

  // Filter frequencies for monthly (≤30 days only)
  const availableFrequencies =
    type === 'Monthly' ? notificationFrequency.filter((f) => f.days <= 30) : notificationFrequency;

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      disableEscapeKeyDown
    >
      <DialogTitle>
        <strong>Edit {type} DPH</strong>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 3,
            }}
          >
            {/* Left Column - Threshold and Frequency */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Threshold */}
              <TextField
                label="Threshold (%)"
                value={threshold}
                onChange={handleThresholdChange}
                placeholder="Threshold in %"
                error={!!thresholdError}
                helperText={thresholdError}
                fullWidth
                type="number"
                inputProps={{ min: 0, max: 100 }}
              />

              {/* Notification Frequency */}
              <FormControl fullWidth>
                <InputLabel>Notification Frequency</InputLabel>
                <Select
                  value={emailFrequency}
                  label="Notification Frequency"
                  onChange={(e) => setEmailFrequency(Number(e.target.value))}
                >
                  {availableFrequencies.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.frequency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Right Column - Email Management */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Email Input */}
              <Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email"
                    fullWidth
                    size="small"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEmail();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={addEmail}
                    size="small"
                    sx={{ minWidth: 120, flexShrink: 0 }}
                  >
                    Add Email
                  </Button>
                </Box>
                {error && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {error}
                  </Typography>
                )}
              </Box>

              {/* Email List */}
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                {emails && emails.length > 0 ? (
                  <List dense>
                    {emails.map((e, i) => (
                      <ListItem
                        key={e}
                        secondaryAction={
                          <IconButton edge="end" onClick={() => removeEmail(i)} title="Delete">
                            <DeleteIcon color="error" />
                          </IconButton>
                        }
                      >
                        <ListItemText primary={e} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No emails added
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" disabled={!!thresholdError} onClick={handleSubmit}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
